import { Box, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { IconCircleCheck } from '@tabler/icons-react';
import { Container, Heading, Text } from '../atomic';

/**
 * @typedef {Object} WhyRSMCProps
 * @property {string} [sectionId] - Section ID for navigation
 */

/**
 * Why RSMC section: who delivers EllaFin and the AWS credentials behind it.
 *
 * @param {WhyRSMCProps} props - Component props
 * @returns {JSX.Element} Why RSMC section
 */
function WhyRSMC({ sectionId = 'why-rsmc' }) {
  const theme = useTheme();

  const credentials = [
    'Passed the AWS Foundational Technical Review',
    'AWS Validated Software, 100% AWS services',
    'Service as software: financial business intelligence delivered as an outcome',
    'Powered by AWS analytics and agentic services, Well-Architected by certified experts',
  ];

  return (
    <Box
      component="section"
      id={sectionId}
      aria-label="Why RSMC"
      sx={{
        py: { xs: 8, md: 12 },
        backgroundColor: theme.custom.footerBackground || '#13152b',
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: 5, md: 8 },
            alignItems: { xs: 'stretch', md: 'center' },
          }}
        >
          {/* Left — Copy */}
          <Box sx={{ flex: '1 1 50%' }}>
            <Typography
              variant="overline"
              sx={{
                display: 'block',
                color: theme.palette.secondary.main,
                fontWeight: 600,
                letterSpacing: '0.1em',
                mb: 1,
              }}
            >
              Why RSMC
            </Typography>
            <Heading
              variant="h2"
              align="left"
              sx={{
                fontSize: { xs: '1.75rem', md: '2.5rem' },
                fontWeight: 700,
                color: '#ffffff',
                mb: 3,
                lineHeight: 1.2,
              }}
            >
              Delivered by an AWS Technology and Consulting Partner.
            </Heading>
            <Text
              sx={{
                color: alpha('#ffffff', 0.75),
                fontSize: { xs: '1rem', md: '1.1rem' },
                lineHeight: 1.7,
                mb: 2,
              }}
            >
              EllaFin is delivered by Ready Secure Managed Cloud. We build on
              AWS, deliver with AWS, and bring the reporting expertise most
              small teams cannot staff in-house.
            </Text>
            <Text
              sx={{
                color: alpha('#ffffff', 0.75),
                fontSize: { xs: '1rem', md: '1.1rem' },
                lineHeight: 1.7,
              }}
            >
              Our platform runs on validated AWS services, so you get enterprise
              reporting on a foundation you can trust.
            </Text>
          </Box>

          {/* Right — Credentials */}
          <Box
            component="ul"
            sx={{
              flex: '1 1 50%',
              listStyle: 'none',
              p: 0,
              m: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            {credentials.map((credential) => (
              <Box
                component="li"
                key={credential}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 2,
                  p: { xs: 2.5, md: 3 },
                  borderRadius: '16px',
                  border: `1px solid ${alpha('#ffffff', 0.12)}`,
                  backgroundColor: alpha('#ffffff', 0.04),
                }}
              >
                <IconCircleCheck
                  size={24}
                  stroke={1.75}
                  color={theme.palette.secondary.main}
                  style={{ flexShrink: 0, marginTop: 2 }}
                />
                <Typography
                  variant="body1"
                  sx={{ color: '#ffffff', lineHeight: 1.6 }}
                >
                  {credential}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

export default WhyRSMC;
